import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import { api, formatApiErrorDetail } from "../lib/api";
import {
  UserCircle, FloppyDisk, CheckCircle, Sparkle, Robot, ArrowsClockwise, WarningCircle,
} from "@phosphor-icons/react";

/**
 * Werknemerprofiel — 6 secties × 1 open vraag. Zelfportret van de coachee
 * als werknemer (wie ben ik, hoe werk ik, wat motiveert me). AI-prefill
 * (max 3x) gebruikt alle testresultaten, CV en de andere twee profielen
 * als input.
 */
export default function CoacheeEmployeeProfilePage() {
  const [coachee, setCoachee] = useState(null);
  const [profile, setProfile] = useState(null);
  const [draft, setDraft] = useState({});
  const [saveState, setSaveState] = useState("idle");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [aiRunning, setAiRunning] = useState(false);
  const [refillConfirm, setRefillConfirm] = useState(false);
  const [refilling, setRefilling] = useState(false);
  const saveTimer = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data: u } = await api.get("/auth/me");
        const { data: list } = await api.get("/coachees");
        const own = list.find((p) => p.user_id === u.id) || null;
        setCoachee(own);
        if (!own) { setLoading(false); return; }
        const { data } = await api.get(`/coachees/${own.id}/employee-profile`);
        setProfile(data);
        setDraft(data.data || {});
        setLoading(false);
        if (data.ai_prefill_available) {
          setAiRunning(true);
          try {
            const { data: filled } = await api.post(
              `/coachees/${own.id}/employee-profile/ai-prefill`,
              {},
              { timeout: 120000 },
            );
            setProfile(filled);
            setDraft(filled.data || {});
          } catch (err) {
            console.warn("Werknemerprofiel AI-prefill mislukt:", err?.message || err);
          } finally {
            setAiRunning(false);
          }
        }
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        setLoading(false);
      }
    })();
  }, []);

  const handleChange = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaveState("saving");
    saveTimer.current = setTimeout(() => persistDraft({ ...draft, [key]: value }), 700);
  };

  const persistDraft = async (data) => {
    if (!coachee) return;
    try {
      const { data: resp } = await api.put(`/coachees/${coachee.id}/employee-profile`, { data });
      setProfile(resp);
      setSaveState("saved");
    } catch (err) {
      console.warn("Werknemerprofiel save failed:", err?.message || err);
      setSaveState("idle");
    }
  };

  const handleBlur = () => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
      persistDraft(draft);
    }
  };

  const handleRefill = async () => {
    if (!coachee || refilling) return;
    setError("");
    setRefilling(true);
    setRefillConfirm(false);
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
      try { await persistDraft(draft); } catch { /* swallow */ }
    }
    try {
      const { data } = await api.post(
        `/coachees/${coachee.id}/employee-profile/ai-prefill?force=true`,
        {},
        { timeout: 120000 },
      );
      setProfile(data);
      setDraft(data.data || {});
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setRefilling(false);
    }
  };

  const completeness = profile?.completeness;
  const prefillSources = profile?.prefill_sources || {};
  const used = profile?.ai_prefill_count ?? 0;
  const max = profile?.ai_prefill_max ?? 3;
  const remaining = profile?.ai_prefill_remaining ?? Math.max(0, max - used);
  const canRefill = profile?.ai_refill_available && remaining > 0;

  if (loading) {
    return <AppLayout title="Werknemerprofiel"><div className="text-zinc-500">Laden...</div></AppLayout>;
  }
  if (aiRunning || refilling) {
    return (
      <AppLayout title="Werknemerprofiel" breadcrumbs={[{ label: "Profielen", to: "/profielen" }, { label: "Werknemerprofiel" }]}>
        <div className="max-w-2xl" data-testid={refilling ? "wn-ai-refilling" : "wn-ai-running"}>
          <div className="neuro-card-bold p-8 bg-white">
            <div className="flex items-start gap-4">
              <div className="shrink-0 inline-flex h-14 w-14 items-center justify-center bg-[#1E88E5]/10 text-[#1E88E5] rounded-sm animate-pulse">
                <Robot size={30} weight="duotone" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest text-[#1E88E5] mb-1">Even geduld</div>
                <h2 className="font-display text-xl font-bold tracking-tight">
                  {refilling ? "De AI vult je werknemerprofiel opnieuw in..." : "De AI vult je werknemerprofiel vast in..."}
                </h2>
                <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
                  We combineren je breintype, competentietest, werkwaarden, conflicttest en
                  CV om voor elk veld jouw zelfportret als werknemer te schetsen. Dit duurt
                  ongeveer 30 seconden.
                </p>
                <div className="mt-4 flex gap-1">
                  <Dot delay={0} /><Dot delay={150} /><Dot delay={300} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }
  if (!profile) {
    return (
      <AppLayout title="Werknemerprofiel" breadcrumbs={[{ label: "Profielen", to: "/profielen" }, { label: "Werknemerprofiel" }]}>
        <div className="text-zinc-500">Geen profiel gevonden.</div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title="Werknemerprofiel"
      breadcrumbs={[{ label: "Profielen", to: "/profielen" }, { label: "Werknemerprofiel" }]}
    >
      <div className="max-w-3xl space-y-6 pb-12" data-testid="employee-profile-page">
        <div className="neuro-card-bold p-5 bg-white">
          <div className="flex items-start gap-3">
            <div className="shrink-0 inline-flex h-12 w-12 items-center justify-center bg-[#1E88E5]/10 text-[#1E88E5] rounded-sm">
              <UserCircle size={26} weight="duotone" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-xl font-bold tracking-tight">Jouw werknemerprofiel</h2>
              <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
                Dit is het zelfportret van wie jij bent als werknemer — je werkidentiteit,
                kernkwaliteiten, werkstijl, drijfveren en valkuilen. Een handige basis voor
                je pitch, LinkedIn of een sollicitatiegesprek. Antwoorden worden automatisch
                opgeslagen.
              </p>
              <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs uppercase tracking-widest text-zinc-500" data-testid="wn-completeness">
                  <strong className={completeness?.percent === 100 ? "text-[#43A047]" : "text-zinc-900"}>
                    {completeness?.filled || 0}
                  </strong>{" "}
                  / {completeness?.total || 0} secties ingevuld
                  {completeness?.percent === 100 && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[#43A047]">
                      <CheckCircle size={14} weight="bold" /> Volledig
                    </span>
                  )}
                </div>
                <SaveIndicator state={saveState} />
              </div>
              {completeness && (
                <div className="mt-2 h-1 bg-zinc-100 rounded-sm overflow-hidden">
                  <div className="h-full bg-[#1E88E5] transition-all" style={{ width: `${completeness.percent}%` }} />
                </div>
              )}
              <RefillBar
                used={used}
                max={max}
                remaining={remaining}
                canRefill={canRefill}
                onClick={() => setRefillConfirm(true)}
              />
            </div>
          </div>
        </div>

        {refillConfirm && (
          <ConfirmModal
            onCancel={() => setRefillConfirm(false)}
            onConfirm={handleRefill}
            remaining={remaining}
            title="Werknemerprofiel opnieuw laten vullen?"
          />
        )}

        {profile.ai_prefill_status === "failed" && (
          <div className="border border-amber-300 bg-amber-50 text-amber-800 text-sm px-4 py-3 rounded-sm flex items-start gap-2" data-testid="wn-ai-failed">
            <Sparkle size={18} weight="duotone" className="shrink-0 mt-0.5 text-amber-600" />
            <div>De AI kon je werknemerprofiel niet automatisch invullen. Geen probleem — vul de velden zelf in of probeer het later opnieuw.</div>
          </div>
        )}

        {error && (
          <div className="border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-4 py-3 rounded-sm" data-testid="wn-error">
            {error}
          </div>
        )}

        {profile.sections.map((section) => (
          <Section
            key={section.id}
            section={section}
            draft={draft}
            onChange={handleChange}
            onBlur={handleBlur}
            prefillSources={prefillSources}
          />
        ))}

        <div className="pt-2">
          <button
            type="button"
            onClick={() => navigate("/profielen")}
            data-testid="wn-back"
            className="inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-2.5 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors"
          >
            <CheckCircle size={16} /> Terug naar profielen
          </button>
        </div>
      </div>
    </AppLayout>
  );
}

function RefillBar({ used, max, remaining, canRefill, onClick }) {
  if (used === 0) return null;
  return (
    <div className="mt-4 pt-4 border-t border-zinc-100 flex flex-wrap items-center justify-between gap-3">
      <div className="text-xs text-zinc-600 flex-1 min-w-[200px]">
        <strong>AI-gebruik: {used} van {max}</strong>
        {remaining > 0
          ? <> — je hebt nog <strong>{remaining}</strong> {remaining === 1 ? "keer" : "keer"} om de AI dit profiel opnieuw te laten vullen.</>
          : <> — limiet bereikt. Bewerk de tekst zelf om verder te verfijnen.</>}
      </div>
      <button
        type="button"
        onClick={onClick}
        disabled={!canRefill}
        data-testid="wn-refill-btn"
        className="inline-flex items-center gap-2 bg-white text-zinc-900 border-2 border-zinc-900 px-4 py-2 text-xs font-semibold tracking-widest uppercase rounded-sm hover:bg-zinc-900 hover:text-white transition-colors disabled:border-zinc-300 disabled:text-zinc-400 disabled:hover:bg-white disabled:cursor-not-allowed"
      >
        <ArrowsClockwise size={14} weight="bold" /> Opnieuw vullen met AI
      </button>
    </div>
  );
}

function ConfirmModal({ onCancel, onConfirm, remaining, title }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="wn-refill-modal" role="dialog" aria-modal="true">
      <div className="bg-white max-w-md w-full p-6 rounded-sm border-2 border-zinc-900">
        <div className="flex items-start gap-3 mb-3">
          <WarningCircle size={24} weight="duotone" className="text-[#1E88E5] shrink-0 mt-0.5" />
          <h3 className="font-display text-lg font-bold tracking-tight">{title}</h3>
        </div>
        <ul className="text-sm text-zinc-700 leading-relaxed space-y-2 mb-3 list-disc pl-5">
          <li>De AI neemt je nieuwste testresultaten en CV mee.</li>
          <li>Velden die <strong>jij zelf hebt ingevuld</strong> blijven onaangetast.</li>
          <li>Lege of eerder AI-voor­ingevulde velden worden bijgewerkt.</li>
        </ul>
        <div className="text-xs text-zinc-500 mb-5">
          Na deze run heb je nog <strong>{Math.max(0, remaining - 1)}</strong> AI-pogingen over.
        </div>
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onCancel} data-testid="wn-refill-cancel"
            className="px-4 py-2 text-xs font-semibold tracking-widest uppercase rounded-sm border-2 border-zinc-300 hover:border-zinc-500 transition-colors">
            Annuleer
          </button>
          <button type="button" onClick={onConfirm} data-testid="wn-refill-confirm"
            className="px-4 py-2 text-xs font-semibold tracking-widest uppercase rounded-sm bg-zinc-900 text-white hover:bg-[#1E88E5] transition-colors inline-flex items-center gap-2">
            <ArrowsClockwise size={14} weight="bold" /> Ja, opnieuw vullen
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ section, draft, onChange, onBlur, prefillSources }) {
  return (
    <div className="neuro-card-bold p-5 bg-white" data-testid={`wn-section-${section.id}`}>
      <h3 className="font-display text-lg font-bold tracking-tight mb-1">{section.title}</h3>
      {section.question && (
        <p className="text-xs text-zinc-500 italic mb-4 leading-relaxed">{section.question}</p>
      )}
      <div className="space-y-4">
        {section.fields.map((f) => (
          <Field
            key={f.key}
            field={f}
            value={draft[f.key] || ""}
            onChange={(v) => onChange(f.key, v)}
            onBlur={onBlur}
            prefillSource={prefillSources[f.key]}
          />
        ))}
      </div>
    </div>
  );
}

function Field({ field, value, onChange, onBlur, prefillSource }) {
  return (
    <div data-testid={`wn-field-${field.key}`}>
      <label className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-xs uppercase tracking-widest text-zinc-700 font-semibold">{field.label}</span>
        {prefillSource && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#1E88E5] bg-[#1E88E5]/10 px-2 py-0.5 rounded-sm border border-[#1E88E5]/30" data-testid={`wn-prefill-${field.key}`}>
            <Sparkle size={11} weight="duotone" /> Voor­ingevuld · {prefillSource}
          </span>
        )}
      </label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={field.placeholder || ""}
        rows={5}
        className="w-full px-3 py-2 border-2 border-zinc-200 rounded-sm text-sm focus:outline-none focus:border-zinc-900 resize-y leading-relaxed"
      />
    </div>
  );
}

function Dot({ delay }) {
  return <span className="inline-block w-2 h-2 rounded-full bg-[#1E88E5] animate-pulse" style={{ animationDelay: `${delay}ms` }} />;
}

function SaveIndicator({ state }) {
  if (state === "saving") {
    return (
      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-400" data-testid="wn-saving">
        <FloppyDisk size={12} weight="duotone" /> Opslaan...
      </span>
    );
  }
  if (state === "saved") {
    return (
      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#43A047]" data-testid="wn-saved">
        <CheckCircle size={12} weight="bold" /> Opgeslagen
      </span>
    );
  }
  return null;
}
